// File handling utilities

export interface FileInfo {
  name: string;
  size: number;
  type: string;
  lastModified: Date;
}

export interface ParseResult {
  data: any[];
  headers: string[];
  rowCount: number;
  errors: string[];
}

// File size utilities
export const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';
  
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

export const parseFileSize = (sizeString: string): number => {
  const units: Record<string, number> = {
    'B': 1,
    'KB': 1024,
    'MB': 1024 * 1024,
    'GB': 1024 * 1024 * 1024
  };
  
  const match = sizeString.match(/^(\d+(?:\.\d+)?)\s*([A-Z]+)$/i);
  if (!match) return 0;
  
  const size = parseFloat(match[1]);
  const unit = match[2].toUpperCase();
  
  return size * (units[unit] || 1);
};

// File type detection
export const getFileType = (file: File): string => {
  const extension = file.name.split('.').pop()?.toLowerCase();
  return extension || 'unknown';
};

export const isSupportedFileType = (file: File, supportedTypes: string[]): boolean => {
  const fileType = getFileType(file);
  return supportedTypes.includes(fileType);
};

// CSV parsing
export const parseCSV = (csvText: string): ParseResult => {
  const errors: string[] = [];
  const lines = csvText.split('\n');
  const data: any[] = [];
  
  if (lines.length === 0) {
    return { data: [], headers: [], rowCount: 0, errors: ['File is empty'] };
  }
  
  // Parse headers
  const headers = parseCSVLine(lines[0]);
  
  // Parse data rows
  for (let i = 1; i < lines.length; i++) {
    const line = lines[i].trim();
    if (line === '') continue; // Skip empty lines
    
    try {
      const values = parseCSVLine(line);
      const row: any = {};
      
      headers.forEach((header, index) => {
        row[header] = values[index] || '';
      });
      
      data.push(row);
    } catch (error) {
      errors.push(`Error parsing line ${i + 1}: ${error}`);
    }
  }
  
  return {
    data,
    headers,
    rowCount: data.length,
    errors
  };
};

// Parse a single CSV line (handles quoted fields)
export const parseCSVLine = (line: string): string[] => {
  const values: string[] = [];
  let current = '';
  let inQuotes = false;
  let i = 0;
  
  while (i < line.length) {
    const char = line[i];
    const nextChar = line[i + 1];
    
    if (char === '"') {
      if (inQuotes && nextChar === '"') {
        // Escaped quote
        current += '"';
        i += 2;
      } else {
        // Toggle quote state
        inQuotes = !inQuotes;
        i++;
      }
    } else if (char === ',' && !inQuotes) {
      // Field separator
      values.push(current.trim());
      current = '';
      i++;
    } else {
      current += char;
      i++;
    }
  }
  
  // Add the last field
  values.push(current.trim());
  
  return values;
};

// JSON parsing
export const parseJSON = (jsonText: string): ParseResult => {
  const errors: string[] = [];
  
  try {
    const parsed = JSON.parse(jsonText);
    let data: any[] = [];
    let headers: string[] = [];
    
    if (Array.isArray(parsed)) {
      data = parsed;
      if (data.length > 0) {
        headers = Object.keys(data[0]);
      }
    } else if (typeof parsed === 'object') {
      // Try to extract array from object
      const arrayKeys = Object.keys(parsed).filter(key => Array.isArray(parsed[key]));
      if (arrayKeys.length > 0) {
        data = parsed[arrayKeys[0]];
        if (data.length > 0) {
          headers = Object.keys(data[0]);
        }
      } else {
        errors.push('JSON does not contain an array');
      }
    } else {
      errors.push('JSON must be an object or array');
    }
    
    return {
      data,
      headers,
      rowCount: data.length,
      errors
    };
  } catch (error) {
    return {
      data: [],
      headers: [],
      rowCount: 0,
      errors: [`Invalid JSON: ${error}`]
    };
  }
};

// File reading utilities
export const readFileAsText = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    
    reader.onload = (event) => {
      const result = event.target?.result;
      if (typeof result === 'string') {
        resolve(result);
      } else {
        reject(new Error('Failed to read file as text'));
      }
    };
    
    reader.onerror = () => {
      reject(new Error('Error reading file'));
    };
    
    reader.readAsText(file);
  });
};

export const readFileAsArrayBuffer = (file: File): Promise<ArrayBuffer> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    
    reader.onload = (event) => {
      const result = event.target?.result;
      if (result instanceof ArrayBuffer) {
        resolve(result);
      } else {
        reject(new Error('Failed to read file as ArrayBuffer'));
      }
    };
    
    reader.onerror = () => {
      reject(new Error('Error reading file'));
    };
    
    reader.readAsArrayBuffer(file);
  });
};

// File parsing based on type
export const parseFile = async (file: File): Promise<ParseResult> => {
  const fileType = getFileType(file);
  
  try {
    const text = await readFileAsText(file);
    
    switch (fileType) {
      case 'csv':
        return parseCSV(text);
      case 'json':
        return parseJSON(text);
      case 'txt':
        // Try to parse as CSV first, then JSON
        try {
          return parseCSV(text);
        } catch {
          return parseJSON(text);
        }
      default:
        return {
          data: [],
          headers: [],
          rowCount: 0,
          errors: [`Unsupported file type: ${fileType}`]
        };
    }
  } catch (error) {
    return {
      data: [],
      headers: [],
      rowCount: 0,
      errors: [`Error reading file: ${error}`]
    };
  }
};

// File download utilities
export const downloadFile = (data: string, filename: string, mimeType: string = 'text/plain'): void => {
  const blob = new Blob([data], { type: mimeType });
  const url = URL.createObjectURL(blob);
  
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  
  // Clean up the URL object
  URL.revokeObjectURL(url);
};

export const downloadCSV = (data: any[], filename: string): void => {
  const csvContent = convertToCSV(data);
  downloadFile(csvContent, filename, 'text/csv');
};

export const downloadJSON = (data: any[], filename: string): void => {
  const jsonContent = JSON.stringify(data, null, 2);
  downloadFile(jsonContent, filename, 'application/json');
};

// Data conversion utilities
export const convertToCSV = (data: any[]): string => {
  if (data.length === 0) return '';
  
  const headers = Object.keys(data[0]);
  const csvRows = [headers.join(',')];
  
  data.forEach(row => {
    const values = headers.map(header => {
      const value = row[header];
      if (value === null || value === undefined) return '';
      if (typeof value === 'string' && (value.includes(',') || value.includes('"'))) {
        return `"${value.replace(/"/g, '""')}"`;
      }
      return value;
    });
    csvRows.push(values.join(','));
  });
  
  return csvRows.join('\n');
};

export const convertToJSON = (data: any[]): string => {
  return JSON.stringify(data, null, 2);
};

// File validation utilities
export const validateFile = (file: File, options: {
  maxSize?: string;
  allowedTypes?: string[];
  requiredFields?: string[];
  enableMemoryManagement?: boolean; // New option for memory management
}): { isValid: boolean; errors: string[] } => {
  const errors: string[] = [];
  
  // Check file size with memory management
  if (options.enableMemoryManagement) {
    // Import the memory validation function
    const { validateFileSizeWithUncompressedEstimate } = require('./validationUtils');
    const memoryValidation = validateFileSizeWithUncompressedEstimate(file);
    if (!memoryValidation.isValid) {
      errors.push(memoryValidation.error || 'File size exceeds memory management limits');
    }
  } else if (options.maxSize) {
    // Use traditional size validation
    const maxSizeBytes = parseFileSize(options.maxSize);
    if (file.size > maxSizeBytes) {
      errors.push(`File size exceeds maximum allowed size of ${options.maxSize}`);
    }
  }
  
  // Check file type
  if (options.allowedTypes && options.allowedTypes.length > 0) {
    const fileType = getFileType(file);
    if (!options.allowedTypes.includes(fileType)) {
      errors.push(`File type '${fileType}' is not supported. Allowed types: ${options.allowedTypes.join(', ')}`);
    }
  }
  
  return {
    isValid: errors.length === 0,
    errors
  };
};

// Generate file info
export const getFileInfo = (file: File): FileInfo => {
  return {
    name: file.name,
    size: file.size,
    type: file.type || getFileType(file),
    lastModified: new Date(file.lastModified)
  };
};

// Create file from data
export const createFileFromData = (data: any[], filename: string, format: 'csv' | 'json' = 'csv'): File => {
  let content: string;
  let mimeType: string;
  
  switch (format) {
    case 'csv':
      content = convertToCSV(data);
      mimeType = 'text/csv';
      break;
    case 'json':
      content = convertToJSON(data);
      mimeType = 'application/json';
      break;
    default:
      throw new Error(`Unsupported format: ${format}`);
  }
  
  return new File([content], filename, { type: mimeType });
};
